import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Sparkle, Users, Lightning, ChartBar,
  ShieldCheck, Database, Envelope, Cloud,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import PrintableComparison from '../components/PrintableComparison';
import SalesConciergeFloat from '../components/SalesConciergeFloat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public marketing homepage at "/"
 * Funnels visitors → Stripe Checkout → auto-provisioned account.
 */
export default function HomePage({ scrollTo = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Authenticated visitors skip the marketing page and go straight to the CRM
  // — UNLESS they intentionally hit /pricing (or another scroll target),
  // in which case we want them to actually see the marketing section.
  useEffect(() => {
    if (scrollTo) return;
    if (user && user !== false) navigate('/contact', { replace: true });
  }, [user, navigate, scrollTo]);

  // Optional auto-scroll target — used by /pricing route to land on the
  // pricing section. Runs once after mount.
  useEffect(() => {
    if (!scrollTo) return;
    const t = setTimeout(() => {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(t);
  }, [scrollTo]);

  const [busy, setBusy] = useState(false);
  const [referralId, setReferralId] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  // Diagnostic banner — visible when ?debug=1 is on the URL. Lets Chip
  // verify Rewardful captured the affiliate cookie WITHOUT spending $25.
  const showDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  // Capture Rewardful referral id once the tracking script is ready (async).
  // Rewardful sets window.Rewardful.referral after making an internal API call,
  // so we MUST wait for the 'ready' event rather than reading synchronously.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const captureReferral = () => {
      try {
        const r = window.Rewardful && window.Rewardful.referral;
        if (r) setReferralId(r);
        // Always update debug snapshot (even when null) so the banner shows live state
        const params = new URLSearchParams(window.location.search);
        setDebugInfo({
          rewardful_loaded: typeof window.rewardful === 'function',
          rewardful_obj_present: !!window.Rewardful,
          referral_id: r || null,
          url_via_param: params.get('via'),
          url_ref_param: params.get('ref'),
          affiliate_param: params.get('affiliate'),
          captured_at: new Date().toISOString(),
        });
      } catch {}
    };
    if (typeof window.rewardful === 'function') {
      try { window.rewardful('ready', captureReferral); } catch {}
    }
    let polls = 0;
    const id = setInterval(() => {
      polls++;
      captureReferral();
      if (polls >= 10) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, []);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showForm, setShowForm] = useState(false);

  const startCheckout = async () => {
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setBusy(true); setError('');
    try {
      // Use the React state captured by the 'rewardful ready' event listener (preferred),
      // with a final synchronous read of window.Rewardful.referral as fallback. The state
      // capture is essential because Rewardful sets window.Rewardful.referral asynchronously
      // (after their internal API call), and reading it inline at click time can race the script.
      let finalReferralId = referralId;
      if (!finalReferralId && typeof window !== 'undefined') {
        // Wait up to ~1.5s for Rewardful to populate the referral if it hasn't yet
        for (let i = 0; i < 6; i++) {
          const r = window.Rewardful && window.Rewardful.referral;
          if (r) { finalReferralId = r; break; }
          await new Promise(res => setTimeout(res, 250));
        }
      }
      const { data } = await axios.post(`${API_URL}/api/billing/checkout/start`, {
        package_id: 'salescrunch_monthly',
        origin_url: window.location.origin,
        email: email.trim(),
        name: name.trim(),
        referral_id: finalReferralId,
      });
      // Redirect to Stripe-hosted checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="home-landing">
      {showDebug && (
        <div className="bg-amber-50 border-b border-amber-300 px-4 py-3 text-xs font-mono" data-testid="affiliate-debug-banner">
          <div className="max-w-6xl mx-auto">
            <div className="font-bold text-amber-900 mb-1">🔧 Affiliate Tracking Debug (admin view — hide by removing ?debug=1)</div>
            {debugInfo ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-0.5 text-amber-900">
                <div>script loaded: <b className={debugInfo.rewardful_loaded ? 'text-emerald-700' : 'text-red-700'}>{String(debugInfo.rewardful_loaded)}</b></div>
                <div>window.Rewardful present: <b className={debugInfo.rewardful_obj_present ? 'text-emerald-700' : 'text-red-700'}>{String(debugInfo.rewardful_obj_present)}</b></div>
                <div>referral id: <b className={debugInfo.referral_id ? 'text-emerald-700' : 'text-red-700'}>{debugInfo.referral_id || '(none — visit ?via=YOUR_SLUG first)'}</b></div>
                <div>?via= URL param: <b>{debugInfo.url_via_param || '(none)'}</b></div>
                <div>?ref= URL param: <b>{debugInfo.url_ref_param || '(none)'}</b></div>
                <div>?affiliate= URL param: <b>{debugInfo.affiliate_param || '(none)'}</b></div>
              </div>
            ) : (
              <div className="text-amber-700">Waiting for Rewardful script to load...</div>
            )}
            <div className="mt-1.5 text-[10px] text-amber-700 italic">
              ✅ Healthy state: script loaded = true, Rewardful object present = true, referral id = a UUID-like string. If you visited with ?via=SLUG, the slug also appears in the URL params row.
            </div>
          </div>
        </div>
      )}
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#002FA7] rounded flex items-center justify-center">
              <span className="text-white font-black text-sm">SC</span>
            </div>
            <span className="text-lg font-black tracking-tight">SalesCrunch<span className="text-[#F39C12]">CRM</span></span>
          </div>
          <nav className="flex items-center gap-5 text-sm">
            <a href="#features" className="text-slate-600 hover:text-slate-900 hidden sm:inline">Features</a>
            <Link to="/walkthrough" className="text-slate-600 hover:text-slate-900 hidden sm:inline" data-testid="home-nav-walkthrough">How it works</Link>
            <Link to="/tutorials" className="text-slate-600 hover:text-slate-900 hidden sm:inline" data-testid="home-nav-tutorials">Tutorials</Link>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 hidden sm:inline">Pricing</a>
            <Link to="/migrationcrunch" className="text-slate-600 hover:text-slate-900 hidden md:inline">MigrationCrunch →</Link>
            <Link to="/login" className="text-slate-700 hover:text-slate-900 font-semibold">Sign in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-20 text-center">
        <div className="inline-flex items-center gap-2 bg-[#F39C12]/10 border border-[#F39C12]/30 rounded-full px-3 py-1 text-xs font-semibold text-[#B8730A] mb-6">
          <Sparkle size={12} weight="fill" />
          The CRM for people who escaped ACT! &mdash; and can&apos;t stomach Salesforce.
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6">
          The ACT! 4.0 you remember,<br />
          <span className="text-[#002FA7]">rebuilt for 2026.</span>
        </h1>
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0B5394] to-[#002FA7] text-white text-xs sm:text-sm font-bold rounded-full px-4 py-1.5 mb-6 shadow-md" data-testid="hero-ai-banner">
          <Sparkle size={14} weight="fill" className="text-[#F39C12]" />
          NEW · Meet <span className="text-[#F39C12]">Crunchy</span>, your built-in AI assistant. Just ask.
        </div>
        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
          Fast. Familiar. Built for people who just want their contacts back.
          The classic three-column contact card, Boolean Lookup, daily alarm popup &mdash;
          all in your browser. <strong className="text-slate-900">Zero learning curve. Try it free for 14 days.</strong>
        </p>

        {!showForm ? (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex flex-col items-center sm:items-start">
              <button
                onClick={() => setShowForm(true)}
                data-testid="home-cta-subscribe"
                className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded-md text-base flex items-center gap-2 transition-colors shadow-sm"
              >
                Start your 14-day free trial <ArrowRight size={18} weight="bold" />
              </button>
              <p className="text-xs text-slate-500 mt-2 sm:ml-1">$0 today &middot; $30/user/month after &middot; cancel anytime</p>
              <p className="text-xs text-slate-500 mt-1 sm:ml-1">
                Curious what&apos;s inside?{' '}
                <Link
                  to="/walkthrough"
                  className="text-[#0B5394] hover:text-[#073763] underline font-semibold"
                  data-testid="home-hero-walkthrough-link"
                >
                  See the full 10-step walkthrough →
                </Link>
              </p>
            </div>
            <Link
              to="/migrationcrunch"
              className="text-slate-700 hover:text-slate-900 underline font-semibold text-sm"
            >
              Coming from another CRM? See MigrationCrunch →
            </Link>
            <Link
              to="/migrate#calculator"
              className="text-slate-500 hover:text-slate-900 text-xs mt-1 block"
              data-testid="home-mc-calculator-link"
            >
              See what you&apos;ll save vs an agency →
            </Link>
          </div>
        ) : (
          <div className="max-w-md mx-auto bg-slate-50 border border-slate-200 rounded-lg p-6 text-left" data-testid="home-checkout-form">
            <h3 className="font-bold text-lg mb-1">Start your 14-day free trial</h3>
            <p className="text-sm text-slate-600 mb-4">
              <strong className="text-slate-900">$0 charged today.</strong> First $30/user charge on day 15. Cancel anytime before then for free.
            </p>
            <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase">Your name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Chip Tippmann"
              className="w-full border border-slate-300 rounded px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="home-name-input"
            />
            <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase">Email *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="w-full border border-slate-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="home-email-input"
            />
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-3">{error}</div>}
            <button
              onClick={startCheckout}
              disabled={busy || !email.trim()}
              data-testid="home-continue-checkout"
              className="w-full bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black font-bold py-3 rounded transition-colors"
            >
              {busy ? 'Opening secure checkout…' : 'Continue to secure checkout →'}
            </button>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Powered by Stripe. We never see your card details.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-500">
          <span className="flex items-center gap-1.5"><Sparkle size={16} weight="fill" className="text-[#F39C12]" /> Crunchy AI included</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-green-500" /> 14-day free trial</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-green-500" /> Cancel anytime</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-green-500" /> 250K+ contacts</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-green-500" /> Real humans on support</span>
        </div>
      </section>

      {/* The story — why SalesCrunch exists (Emmy positioning, Jul 2026) */}
      <section className="px-6 py-16 bg-white" data-testid="home-story-section">
        <div className="max-w-3xl mx-auto text-left">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#CC6600] mb-3">Why SalesCrunch exists</p>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-6" data-testid="home-story-tagline">
            Remember everything. Miss nothing.<br className="hidden sm:block" /> No training required.
          </h2>
          <div className="border-l-4 border-[#0B5394] pl-5 mb-6">
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed" data-testid="home-story-paragraph">
              He said <em>&ldquo;call me in two weeks and I&rsquo;ll order one &mdash; then in about a year
              I&rsquo;ll get one more.&rdquo;</em> Six months later, most salespeople have lost that promise in
              a spreadsheet. SalesCrunch pops the alarm on the right day, shows you his exact words, and
              Crunchy briefs you before you dial. You walk in sounding like you never forgot &mdash;
              because you didn&rsquo;t have to remember.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="home-story-beat-1">
              <p className="font-bold text-slate-900 mb-1">⏰ The alarm fires</p>
              <p className="text-slate-600">On the exact day you promised to call &mdash; not buried in a pipeline report.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="home-story-beat-2">
              <p className="font-bold text-slate-900 mb-1">📝 His exact words</p>
              <p className="text-slate-600">Every note, date-stamped, newest first &mdash; right at the bottom of his card, ACT!-style.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="home-story-beat-3">
              <p className="font-bold text-slate-900 mb-1">🤖 Crunchy briefs you</p>
              <p className="text-slate-600">A 10-second pre-call brief of everything you know &mdash; so you dial sounding sharp.</p>
            </div>
          </div>
          <div className="mt-8">
            <Link
              to="/manifesto"
              className="inline-flex items-center gap-2 text-[#0B5394] font-bold hover:underline"
              data-testid="home-manifesto-link"
            >
              Read the Anti-Salesforce Manifesto — why we have no pipelines →
            </Link>
          </div>
        </div>
      </section>

      {/* Gemini quote band — third-party credibility teaser → /gemini */}
      <section className="bg-slate-50 border-y border-slate-200 px-6 py-8" data-testid="home-gemini-band">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center sm:text-left">
          <p className="text-base sm:text-lg font-black text-slate-800 leading-snug">
            &ldquo;The speed and familiarity of <span className="text-[#002FA7]">1998</span> with the
            automation power of <span className="text-[#002FA7]">2026</span>.&rdquo;
            <span className="block sm:inline text-sm font-semibold text-slate-500 sm:ml-2">— Google Gemini, reviewing SalesCrunchCRM</span>
          </p>
          <Link
            to="/gemini"
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-[#002FA7] hover:text-[#0B5394] underline underline-offset-4"
            data-testid="home-gemini-band-link"
          >
            Read what Gemini said →
          </Link>
        </div>
      </section>

      {/* Features grid */}
      {/* Meet Crunchy — AI Concierge spotlight */}
      <section className="bg-gradient-to-br from-[#0B5394] via-[#073763] to-[#002FA7] text-white px-6 py-20 border-y border-[#001f6e]" data-testid="crunchy-spotlight">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F39C12]/20 border border-[#F39C12]/40 rounded-full px-3 py-1 text-xs font-bold text-[#F39C12] mb-4">
                <Sparkle size={12} weight="fill" /> SAY HELLO
              </div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-4 leading-tight">
                Meet <span className="text-[#F39C12]">Crunchy</span>.
              </h2>
              <p className="text-lg text-slate-200 mb-5 leading-relaxed">
                Other CRMs have an AI chatbot. <strong className="text-white">SalesCrunchCRM has a concierge.</strong>
              </p>
              <p className="text-base text-slate-300 mb-6 leading-relaxed">
                Crunchy knows your CRM cold &mdash; every feature, every shortcut, every workflow.
                Ask in plain English, and he actually <em>does the thing</em>. No menus to dig through.
                No 400-page manual. No "have you tried turning it off and on again?"
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Sparkle size={14} weight="fill" className="text-[#F39C12] shrink-0 mt-1" />
                  <span><strong className="text-white">Finds things.</strong> <span className="text-slate-300">"Who haven&apos;t I called in 60 days in Indiana?"</span></span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Sparkle size={14} weight="fill" className="text-[#F39C12] shrink-0 mt-1" />
                  <span><strong className="text-white">Schedules things.</strong> <span className="text-slate-300">"Set a follow-up call with Bob Jones for Tuesday at 2pm."</span></span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Sparkle size={14} weight="fill" className="text-[#F39C12] shrink-0 mt-1" />
                  <span><strong className="text-white">Explains anything.</strong> <span className="text-slate-300">"How do I print mailing labels?" &mdash; and he walks you through it.</span></span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Sparkle size={14} weight="fill" className="text-[#F39C12] shrink-0 mt-1" />
                  <span><strong className="text-white">Writes marketing copy.</strong> <span className="text-slate-300">"Write me a landing page for my new product" &mdash; hero, emails, tweets, ads, video script &mdash; in 15 seconds.</span></span>
                </div>
              </div>

              {/* Public demo CTA — lets prospects try Crunchy's writing before
                  they sign up. Rate-limited 3/IP/day server-side. */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/tools/marketing-generator"
                  className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-5 py-3 rounded transition-colors text-sm"
                  data-testid="crunchy-try-marketing-gen"
                >
                  <Sparkle size={14} weight="fill" />
                  Try Crunchy free &mdash; generate marketing copy in 15 seconds
                </Link>
              </div>

              <p className="text-xs text-slate-400 mt-6 italic">
                Powered by Claude Sonnet 4.5. 150 messages/month included &mdash; plenty for most folks. Trained on every feature of SalesCrunchCRM, not the public internet.
              </p>
            </div>

            {/* Mock chat preview */}
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-white/10">
              <div className="bg-gradient-to-r from-[#0B5394] to-[#002FA7] px-4 py-3 flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-[#F39C12]" />
                <div className="flex-1">
                  <div className="font-bold text-white text-sm leading-tight">Crunchy</div>
                  <div className="text-[10px] text-white/70 leading-tight">Your CRM concierge &middot; online</div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full" />
              </div>
              <div className="bg-slate-50 p-4 space-y-2 text-xs">
                <div className="flex justify-end">
                  <div className="bg-[#0B5394] text-white rounded-lg px-3 py-2 max-w-[80%]">
                    Who haven&apos;t I called in 90 days in Indiana?
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 max-w-[80%] shadow-sm text-slate-800">
                    <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-wide">🔧 list stale contacts</div>
                    Found <strong>17 contacts</strong> in Indiana you haven&apos;t called in 90+ days. Top 3:
                    <div className="mt-1.5 space-y-0.5 text-slate-600">
                      <div>• Bob Jones &mdash; Indianapolis (last call: 142 days)</div>
                      <div>• Mary Smith &mdash; Fort Wayne (last call: 118 days)</div>
                      <div>• Tom Walker &mdash; Bloomington (last call: 95 days)</div>
                    </div>
                    Want me to schedule calls? 👍
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#0B5394] text-white rounded-lg px-3 py-2 max-w-[80%]">
                    Yes &mdash; call Bob next Tuesday at 10am
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 max-w-[80%] shadow-sm text-slate-800">
                    <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-wide">🔧 schedule activity</div>
                    Done! Call with <strong>Bob Jones</strong> on Tue, May 26 at 10:00 AM ☎️
                  </div>
                </div>
              </div>
              <div className="bg-white border-t border-slate-200 p-2 flex items-center gap-2">
                <input type="text" disabled placeholder="Ask Crunchy anything…" className="flex-1 text-xs bg-slate-50 rounded px-2 py-1.5 text-slate-400" />
                <Sparkle size={14} weight="fill" className="text-[#0B5394]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRUNCHY FIRST-PERSON — Crunchy speaking directly to the visitor.
          Placed after the 3rd-person spotlight so the flow reads like:
          Chip introduces Crunchy → Crunchy introduces himself. The
          self-aware "what I don't do" block is intentional — nobody
          else does this, and it's the biggest trust-builder we have. */}
      <section
        className="bg-slate-50 border-b border-slate-200 px-6 py-20"
        data-testid="crunchy-first-person"
      >
        <div className="max-w-5xl mx-auto">
          {/* Speech-bubble header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-white border-2 border-slate-900 rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-wider text-slate-900 mb-6 shadow-sm">
              <span className="text-lg leading-none">🍿</span> Hear from Crunchy himself
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-6 leading-tight">
              &ldquo;Hi, I&apos;m Crunchy.&rdquo;
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
              I live in the bottom-right corner of your SalesCrunchCRM.
              Click me and I show up. That&apos;s basically it.
            </p>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed mt-4">
              Here&apos;s what I actually do — in plain English, no jargon,
              no over-promising:
            </p>
          </div>

          {/* What Crunchy does — 4 cards */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="crunchy-cando-grid">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-crm">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I know your CRM
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                <em className="text-slate-900 font-semibold">&ldquo;Who haven&apos;t I called in Indiana in 60 days?&rdquo;</em>
                {' '}Give me that. I&apos;ll pull the list. Draft the outreach
                email too, if you want. That&apos;s a good day&apos;s work
                in about 8 seconds.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-writes">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I write in your voice
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Cold outreach, follow-ups, LinkedIn posts, blog outlines,
                positioning paragraphs. I write like a human — no
                &ldquo;circle back&rdquo; or &ldquo;touch base.&rdquo; I
                write the way you would if you had the time.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-tools">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I know the AI tool map
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Slide deck? <strong>Gamma.</strong> Voice message?
                {' '}<strong>ElevenLabs.</strong> Meeting notes?
                {' '}<strong>Fathom.</strong> Video walk-through?
                {' '}<strong>Loom.</strong> I&apos;ll point you to the
                right one for the job. One pick. Not a list of ten.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-brief">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I brief you before every call
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Open a contact with a meeting today and I&apos;ve already read
                the file: who they are, where you left off, and a suggested
                opening line. The 15 minutes you used to spend digging through
                notes? Keep them.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-ghostbuster">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I hunt the ghosts in your pipeline
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Deals that went quiet for two weeks don&apos;t hide from me. I
                flag them on your pipeline and draft the re-engagement email
                from your own notes — personal, specific, zero
                &ldquo;just checking in.&rdquo; One click and it&apos;s in your
                composer.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="crunchy-card-brainstorm">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                I brainstorm like a real person
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                <em className="text-slate-900 font-semibold">&ldquo;Give me 3 positioning angles for a bookkeeper tired of big-name firms.&rdquo;</em>
                {' '}I&apos;ll give you three. Different angles. Pick one,
                ignore the others. I don&apos;t get offended.
              </p>
            </div>
          </div>

          {/* What Crunchy does NOT do — the trust-builder */}
          <div className="bg-slate-900 text-white rounded-lg p-6 md:p-8" data-testid="crunchy-cannot-do">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-3">
              What I don&apos;t do (because faking it is worse)
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-slate-300 leading-relaxed">
              <div>
                <strong className="text-white">Legal, tax, or medical stuff.</strong>{' '}
                Call a real lawyer, CPA, or doctor. Happy to help you
                draft the email <em>to</em> them though.
              </div>
              <div>
                <strong className="text-white">Live web searches.</strong>{' '}
                No stock prices, no news, no weather. Chip&apos;s working
                on it. For now, use Google.
              </div>
              <div>
                <strong className="text-white">Make things up.</strong>{' '}
                If I don&apos;t know, I say so. If I&apos;m guessing, I
                flag it. That&apos;s a promise.
              </div>
              <div>
                <strong className="text-white">Sell you out.</strong>{' '}
                If I recommend a tool, it&apos;s because it&apos;s the
                right one — not because someone paid me to say it.
              </div>
            </div>
          </div>

          {/* Sign-off */}
          <p className="text-center text-slate-600 mt-10 text-sm italic max-w-xl mx-auto">
            The point: Chip built SalesCrunchCRM for small business owners who
            never want to touch a bloated CRM again. He built me for small
            business owners who know they should be using AI but don&apos;t
            know where to start.
            <br />
            <span className="text-slate-900 font-bold not-italic mt-2 inline-block">
              I&apos;m right there in the corner. Give me a try. 🍿
            </span>
          </p>
        </div>
      </section>

      {/* Feature grid */}
      <section id="features" className="bg-slate-50 border-y border-slate-200 px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center tracking-tight mb-3">
            Everything ACT! 4.0 had. Nothing it didn&apos;t.
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
            We rebuilt the iconic 1990s ACT! CRM with modern speed and cloud sync &mdash;
            without the $1,200/year &quot;Growth Suite&quot; nonsense.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon={<Users size={24} weight="fill" />} title="Three-column contact card"
              text="The exact ACT! 4.0 layout. Phone, address, notes, history &mdash; all on one screen. No clicking through tabs to see what your customer told you last month." />
            <Feature icon={<Lightning size={24} weight="fill" />} title="Boolean Lookup"
              text="Find &quot;all customers in Texas with 'plumbing' in their notes who haven't been called in 90 days&quot; in 5 seconds. The killer feature ACT! perfected. We kept it." />
            <Feature icon={<ChartBar size={24} weight="fill" />} title="Sales pipeline"
              text="Drag-drop kanban for deals. 6-stage pipeline out of the box. Reports show you exactly what's moving, what's stuck, and where to push." />
            <Feature icon={<ShieldCheck size={24} weight="fill" />} title="Daily alarm popup"
              text="Overdue tasks roll forward automatically every morning, oldest first. Snooze, complete, reschedule with a single click. Just like ACT! &mdash; no extension required." />
            <Feature icon={<Database size={24} weight="fill" />} title="Custom fields, your way"
              text="10 built-in user fields plus unlimited custom fields. 5 field types &mdash; text, dropdown, checkbox, date, number. Reusable dropdown lists update once, apply everywhere." />
            <Feature icon={<Envelope size={24} weight="fill" />} title="Bulk mail merge + Avery labels"
              text="Email blasts that don't get flagged as spam (real personalization, not 'Hi {{first_name}}'). Avery 5160-5163 labels and #10/A2/A7 envelopes print perfectly." />
            <Feature icon={<Cloud size={24} weight="fill" />} title="250,000-contact tested"
              text="Bring your entire 25-year customer database. We've stress-tested it at quarter-million contacts on a single account. No tier upgrades needed." />
            <Feature icon={<Sparkle size={24} weight="fill" />} title="AI-powered import (free)"
              text="Old ACT! .dbf files? Goldmine? Outlook .pst? Drop them in &mdash; our AI maps fields, matches notes to contacts, and saves you 3 weeks of cleanup. Built in. Free." />
            <Feature icon={<CheckCircle size={24} weight="fill" />} title="Real-time team sync"
              text="Your assistant adds a contact, you see it instantly. Activities logged on the road show up on your desktop in seconds. WebSocket-powered. Just works." />
          </div>
        </div>
      </section>

      {/* Comparison — full side-by-side table with print/email/copy-link toolbar */}
      <PrintableComparison
        variant="light"
        testid="home-crm-compare"
        title="How SalesCrunchCRM stacks up against the giants"
        subtitle="Public list pricing as of Feb 2026. The big-name CRMs charge $25–$330/user/mo for features 80% of SMBs never use. We built the opposite."
        shareSubject="SalesCrunchCRM vs Salesforce / HubSpot / Pipedrive / Act! — side-by-side"
        columns={[
          { id: 'sc',  label: 'SalesCrunchCRM', sub: 'ACT! 4.0 reborn',         isUs: true },
          { id: 'sf',  label: 'Salesforce',     sub: 'Enterprise default'                   },
          { id: 'hs',  label: 'HubSpot',        sub: 'Marketing-first'                      },
          { id: 'pd',  label: 'Pipedrive',      sub: 'Pipeline-first'                       },
          { id: 'act', label: 'Act! Premium',   sub: 'Legacy desktop'                       },
        ]}
        rows={[
          { label: 'Price / user / mo', highlight: true, cells: {
            sc:  <span className="text-emerald-700 font-black">$30/user</span>,
            sf:  '$25 – $330',
            hs:  '$0 – $150+ (contact tiers)',
            pd:  '$14 – $79',
            act: '$30 – $60',
          }},
          { label: 'Annual contract required', cells: {
            sc:  <span className="text-emerald-700">No · cancel anytime</span>,
            sf:  'Yes',
            hs:  'Usually yes',
            pd:  'Per-user, monthly OK',
            act: 'Yes',
          }},
          { label: 'Migration of full history',  highlight: true, cells: {
            sc:  <span className="text-emerald-700 font-bold">Free, unlimited, 99% accuracy</span>,
            sf:  '$5,000 – $50,000',
            hs:  '$3,000 – $25,000',
            pd:  '$500 – $5,000',
            act: '$1,500 – $9,000',
          }},
          { label: 'Lookup speed @ 250k contacts', cells: {
            sc:  <span className="text-emerald-700 font-bold">Under 1 second</span>,
            sf:  '5 – 15 s',
            hs:  'Moderate',
            pd:  'Fast (small DBs)',
            act: 'Very slow',
          }},
          { label: 'AI agent included', cells: {
            sc:  <span className="text-emerald-700 font-bold">Crunchy — included</span>,
            sf:  'Agentforce: +$550/user/mo',
            hs:  'Enterprise tier only',
            pd:  'Add-on cost',
            act: 'Limited add-on',
          }},
          { label: 'AI can execute tasks (not just chat)', cells: {
            sc:  <span className="text-emerald-700">Yes — "email all cold leads"</span>,
            sf:  'Predictive, mostly',
            hs:  'Content generation',
            pd:  'Basic insights',
            act: 'No',
          }},
          { label: 'Notes & history depth', cells: {
            sc:  <span className="text-emerald-700">Unlimited</span>,
            sf:  'Unlimited (at cost)',
            hs:  'Limited by tier',
            pd:  'Limited',
            act: 'Throttled',
          }},
          { label: 'Onboarding time', cells: {
            sc:  <span className="text-emerald-700 font-bold">Same day</span>,
            sf:  'Weeks — needs consultant',
            hs:  'Days',
            pd:  'Hours',
            act: 'Days — old UX',
          }},
          { label: 'Sweet-spot user', cells: {
            sc:  <span className="text-emerald-700">Power users, CRM refugees</span>,
            sf:  'Large enterprises',
            hs:  'Marketing teams',
            pd:  'Simple sales pipelines',
            act: 'Legacy Act! users',
          }},
          { label: '10-user team annual cost (est.)', highlight: true, cells: {
            sc:  <span className="text-emerald-700 font-black">$3,000</span>,
            sf:  '$21,000+',
            hs:  '$18,000+',
            pd:  '$9,480',
            act: '$7,200',
          }},
        ]}
        footnote="Pricing reflects publicly listed prices as of Feb 2026. Annual cost estimate assumes mid-tier plan for 10 users. Migration cost assumes 50,000-contact database with active note history."
      />


      {/* Pricing CTA */}
      <section id="pricing" className="bg-[#002FA7] text-white px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">One plan. One price. No surprises.</h2>
          <div className="text-6xl font-black mb-2">$30<span className="text-2xl font-normal text-white/70">/user/month</span></div>
          <p className="text-white/80 mb-2 max-w-xl mx-auto">
            Everything above. Up to 250,000 contacts. Cancel anytime from your account settings. No long-term contract.
          </p>
          <p className="text-[#F39C12] font-bold mb-8">First 14 days free &middot; $0 charged today.</p>
          <button
            onClick={() => { setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            data-testid="home-cta-pricing-subscribe"
            className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded-md text-base inline-flex items-center gap-2 transition-colors"
          >
            Start your 14-day free trial <ArrowRight size={18} weight="bold" />
          </button>
          <p className="text-xs text-white/60 mt-4">Secure checkout by Stripe. We never see your card.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 px-6 py-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#002FA7] rounded flex items-center justify-center">
                <span className="text-white font-black text-sm">SC</span>
              </div>
              <span className="text-base font-black text-white">SalesCrunch<span className="text-[#F39C12]">CRM</span></span>
            </div>
            <p className="text-xs">The ACT! 4.0 you remember, rebuilt for 2026. Made for the rest of us.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Product</h4>
            <ul className="space-y-1.5 text-xs">
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><Link to="/walkthrough" className="hover:text-white" data-testid="home-footer-walkthrough">How it works</Link></li>
              <li><Link to="/tutorials" className="hover:text-white" data-testid="home-footer-tutorials">Video tutorials</Link></li>
              <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              <li><Link to="/migrationcrunch" className="hover:text-white">MigrationCrunch</Link></li>
              <li><a href="https://crmcomparisons.com" target="_blank" rel="noopener" className="hover:text-white" data-testid="footer-crmcomparisons-link">CRM comparisons — how we stack up</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Company</h4>
            <ul className="space-y-1.5 text-xs">
              <li><Link to="/contact-us" className="hover:text-white" data-testid="footer-contact-link">Contact</Link></li>
              <li><Link to="/blog" className="hover:text-white" data-testid="footer-blog-link">Blog — the anti-Salesforce essays</Link></li>
              <li><Link to="/ai-agents" className="hover:text-white" data-testid="footer-ai-agents-link">AI agents — built in, not bolted on</Link></li>
              <li><Link to="/affiliates" className="hover:text-white" data-testid="footer-affiliate-link">Affiliate program — earn $720/customer</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Legal</h4>
            <ul className="space-y-1.5 text-xs">
              <li>
                <a
                  href="https://app.termly.io/policy-viewer/policy.html?policyUUID=1d95cd3a-8063-4c28-b4e8-3f0253c39da8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                  data-testid="footer-terms-link"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="https://app.termly.io/policy-viewer/policy.html?policyUUID=31b34c66-4334-40a3-b0ad-bfb742f1940f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://app.termly.io/policy-viewer/policy.html?policyUUID=6fdd2b55-3320-465b-ae10-91351eeaebb3"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                  data-testid="footer-cookies-link"
                >
                  Cookie Policy
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => { try { window.displayPreferenceModal && window.displayPreferenceModal(); } catch {} }}
                  className="hover:text-white text-left"
                  data-testid="footer-cookie-preferences-link"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-6 border-t border-slate-800 text-xs">
          © 2026 SalesCrunchCRM. All rights reserved.
        </div>
      </footer>

      {/* Crunchy the salesperson — floating pre-sales chat */}
      <SalesConciergeFloat />
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-[#002FA7] hover:shadow-sm transition-all">
      <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] rounded flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-bold text-slate-900 mb-1.5">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
